import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { ThemePalette } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { PaymentMethod, PaymentMethodItem, PaymentMethods, ProcessorName } from '@cargos/sprintpay-models';
import { PaymentMethodsRequestService } from '@cargos/sprintpay-services';
import { Observable, Subject, from, of, switchMap, take, takeUntil } from 'rxjs';
import { PaymentMethodsService } from 'src/app/shared/services';
import { NotificationsService } from 'src/app/shared/services/notification.service';
import Swal from 'sweetalert2';
import { PaymentMethodsHandlerService } from '../../shared/services/payment-methods-handler.service';
import { LastFourDigitsComponent } from '../last-four-digits/last-four-digits.component';
import { LogoPaymentMethodComponent } from '../logo-payment-method/logo-payment-method.component';
import { EcheckListtMaterialModule } from './echeck-material-modules.module';
import { EmptyEcheckistComponent } from './empty-echeck-list/empty-echeck-list.component';

@Component({
    standalone: true,
    imports: [
        EcheckListtMaterialModule,
        LogoPaymentMethodComponent,
        LastFourDigitsComponent,
        EmptyEcheckistComponent,
        MatButtonModule,
        MatTooltipModule,
    ],
    selector: 'app-echecks-list',
    templateUrl: './echeck.component.html',
    styleUrls: ['./echeck.component.scss'],
})
export class EcheckListComponent implements OnInit, OnDestroy {
    public echecks?: (PaymentMethodItem & { color?: ThemePalette })[];
    public token: string | null = null;
    public paymentMethodsEnum = PaymentMethods;
    public isExpired: boolean;
    private unsubscribe$: Subject<void>;

    /**
     * This variable do not define is the cart is above the threshold, is a configuration to get the payment method selected based on section
     */
    @Input() aboveThreshold: boolean = false;
    @Input() loading: boolean;
    @Input() echeckDeletionAllowed: boolean = false;
    @Input() displayEmptyList: boolean = false;
    @Output() eventOnSelectEcheck: EventEmitter<string> = new EventEmitter<string>();
    @Output() echeck = new EventEmitter<PaymentMethodItem>();

    constructor(
        private paymentMethodsService: PaymentMethodsService,
        private paymentMethodsHandlerService: PaymentMethodsHandlerService,
        private notificationsService: NotificationsService,
        private paymentMethodsRequestService: PaymentMethodsRequestService,
        private router: Router
    ) {
        this.unsubscribe$ = new Subject<void>();
    }

    ngOnInit(): void {
        this.echeckExpired();
        this.subscribeEchecks();
        this.subscribeEcheckItemSelected();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    subscribeEchecks(): void {
        this.paymentMethodsService
            .getPaymentMethodsByType$(PaymentMethods.ECHECK)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (echecks: PaymentMethod | undefined) => {
                    this.echecks =
                        echecks?.items.map((echeck: PaymentMethodItem & { color?: ThemePalette }) => {
                            const color: ThemePalette = this.isExpired ? 'warn' : 'accent';
                            return { ...echeck, color };
                        }) || [];
                    this.echecks = this.echecks.sort((echeckA, echeckB) => {
                        if (
                            echeckA.accountingDetails.isVerified != undefined &&
                            echeckB.accountingDetails.isVerified != undefined
                        ) {
                            if (echeckA.accountingDetails.isVerified > echeckB.accountingDetails.isVerified) {
                                return -1;
                            }
                            if (echeckA.accountingDetails.isVerified < echeckB.accountingDetails.isVerified) {
                                return 1;
                            }
                        }
                        return 0;
                    });
                },
            });
    }

    subscribeEcheckItemSelected(): void {
        this.paymentMethodsHandlerService
            .getPaymentMethodSelected$(this.aboveThreshold)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (paymentMethodSelected) => {
                    this.token = paymentMethodSelected?.token || null;
                },
            });
    }

    selectPaymentMethod(token?: string | null, type?: unknown): void {
        if (token && type != 'warn') {
            this.eventOnSelectEcheck.emit(token);
        }
    }

    removeEcheck(echeck: PaymentMethodItem): void {
        if (!echeck.paymentToken?.token) {
            return;
        }

        from(
            Swal.fire({
                title: 'Are you sure?',
                text: "You won't be able to revert this!",
                showCancelButton: true,
                showConfirmButton: true,
                confirmButtonText: 'Yes, delete it!',
                reverseButtons: true,
            })
        )
            .pipe(
                switchMap((result) => {
                    if (!result.isDenied) {
                        return of(null);
                    }
                    this.loading = true;

                    return this.confirmRemoveEcheck(echeck).pipe(take(1));
                })
            )
            .subscribe({
                next: (deletionConfirmed) => {
                    this.loading = false;
                    if (deletionConfirmed) {
                        Swal.fire('Removed!', '', 'success');
                    }
                },
                error: () => {
                    this.loading = false;
                },
            });
    }

    confirmRemoveEcheck(echeck: PaymentMethodItem): Observable<string> {
        if (echeck.paymentToken?.paymentProcessor?.name === ProcessorName.Forte) {
            return this.paymentMethodsRequestService.removeEcheck(echeck.paymentToken?.token || '');
        }
        return this.paymentMethodsRequestService.removeBankAccount(Number(echeck.paymentToken?.token));
    }

    reverifyECheck(echeck: PaymentMethodItem): void {
        this.selectPaymentMethod(null);
        this.paymentMethodsService.setValidateEcheckState(echeck);
        this.router.navigate(['/cart/echeck-user']);
    }

    private echeckExpired(): void {
        const banners = this.notificationsService.getBanners();
        const alerts = this.notificationsService.getPopups();
        if (banners.length || alerts.length) {
            this.isExpired =
                banners?.some(
                    (notfication) => notfication.name === 'Regions migration' && notfication.styleClass === 'error'
                ) ||
                alerts?.some(
                    (notfication) => notfication.name === 'Regions migration' && notfication.styleClass === 'error'
                );
        }
    }
}
